import { useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import type { ClientMessage, ServerMessage } from '../../../shared/types';
import { navigateToHome, navigateToRoom, roomCodeFromUrl } from '../router';
import { ensureSession } from '../auth/session';

/** Same-origin WS URL: https://host/snake-ladder → wss://host/snake-ladder/ws.
 *  In production Caddy strips the base path, so the server sees /ws. */
function resolveWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const base = import.meta.env.BASE_URL || '/';
  const path = base.replace(/\/$/, '') + '/ws';
  return `${proto}://${window.location.host}${path}`;
}

const RETRY_BASE_MS = 800;
const RETRY_MAX_MS = 8000;

const STALE_SEAT_CODES = new Set(['ROOM_NOT_FOUND', 'AUTH_FAILED']);

export interface GameSocketApi {
  createRoom: () => void;
  joinRoom: (roomCode: string) => void;
  setReady: (ready: boolean) => void;
  startGame: () => void;
  setTimeLimit: (seconds: number) => void;
  rollDice: () => void;
  sendChat: (text: string) => void;
  leave: () => void;
  rejoin: () => void;
  refreshSession: () => Promise<void>;
  addBot: (difficulty: 'easy' | 'medium' | 'hard') => void;
  removeBot: (playerId: string) => void;
  setBotDifficulty: (playerId: string, difficulty: 'easy' | 'medium' | 'hard') => void;
  socket: WebSocket | null;
}

export function useGameSocket(): GameSocketApi {
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(1);
  const retryTimerRef = useRef<number | null>(null);
  const closedByUser = useRef(false);
  const mountedRef = useRef(false);
  const refreshVersionRef = useRef(0);
  const openVersionRef = useRef(0);
  const pendingRefreshOpenRef = useRef<(() => void) | null>(null);
  const store = useGameStore;

  const openSocket = useCallback((onOpen?: () => void) => {
    if (closedByUser.current || !mountedRef.current) return;
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const openVersion = ++openVersionRef.current;

    void ensureSession().finally(() => {
      if (
        closedByUser.current ||
        !mountedRef.current ||
        openVersionRef.current !== openVersion
      ) {
        return;
      }
      const ws = new WebSocket(resolveWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 1;
        store.getState().setConnected(true);
        store.getState().setError(null);
        sendReconnect(ws);
        pendingRefreshOpenRef.current?.();
        pendingRefreshOpenRef.current = null;
        onOpen?.();
      };

      ws.onmessage = (event) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(event.data as string);
        } catch {
          return;
        }
        handleServerMessage(msg);
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        store.getState().setConnected(false);
        if (closedByUser.current || !mountedRef.current) return;
        const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * retryRef.current);
        retryRef.current *= 2;
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          openSocket(onOpen);
        }, delay);
      };

      ws.onerror = () => ws.close();
    });
  }, [store]);

  const connect = useCallback(() => {
    openSocket();
  }, [openSocket]);

  useEffect(() => {
    mountedRef.current = true;
    closedByUser.current = false;
    connect();
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState > 1)) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      mountedRef.current = false;
      closedByUser.current = true;
      refreshVersionRef.current += 1;
      openVersionRef.current += 1;
      pendingRefreshOpenRef.current = null;
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const rejoin = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) sendReconnect(ws);
  }, []);

  const refreshSession = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const version = ++refreshVersionRef.current;
        const current = wsRef.current;
        closedByUser.current = true;
        if (retryTimerRef.current !== null) {
          window.clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        let settled = false;
        const timeout = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          if (pendingRefreshOpenRef.current === opened) {
            pendingRefreshOpenRef.current = null;
          }
          reject(new Error('The name was saved, but reconnecting timed out.'));
        }, 10_000);
        const opened = () => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          resolve();
        };
        pendingRefreshOpenRef.current = opened;
        const reopen = () => {
          if (!mountedRef.current || refreshVersionRef.current !== version) {
            if (!settled) {
              settled = true;
              window.clearTimeout(timeout);
              if (pendingRefreshOpenRef.current === opened) {
                pendingRefreshOpenRef.current = null;
              }
              reject(new Error('Session refresh was cancelled.'));
            }
            return;
          }
          if (wsRef.current === current) wsRef.current = null;
          closedByUser.current = false;
          retryRef.current = 1;
          openSocket(opened);
        };
        if (!current || current.readyState === WebSocket.CLOSED) {
          reopen();
          return;
        }
        current.addEventListener('close', reopen, { once: true });
        current.close();
      }),
    [openSocket],
  );

  const api: GameSocketApi = {
    socket: wsRef.current,
    createRoom: () => send({ type: 'CREATE_ROOM' }),
    joinRoom: (roomCode) => send({ type: 'JOIN_ROOM', roomCode }),
    setReady: (ready) => send({ type: 'SET_READY', ready }),
    startGame: () => send({ type: 'START_GAME' }),
    setTimeLimit: (seconds) => send({ type: 'SET_TIME_LIMIT', seconds }),
    rollDice: () => send({ type: 'ROLL_DICE' }),
    sendChat: (text) => send({ type: 'SEND_CHAT', text }),
    addBot: (difficulty) => send({ type: 'ADD_BOT', difficulty }),
    removeBot: (playerId) => send({ type: 'REMOVE_BOT', playerId }),
    setBotDifficulty: (playerId, difficulty) => send({ type: 'SET_BOT_DIFFICULTY', playerId, difficulty }),
    rejoin,
    refreshSession,
    leave: () => {
      refreshVersionRef.current += 1;
      closedByUser.current = true;
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      const s = store.getState();
      s.reset();
      navigateToHome();
      if (wsRef.current) wsRef.current.close();
      window.setTimeout(() => {
        if (!mountedRef.current) return;
        closedByUser.current = false;
        connect();
      }, 400);
    },
  };
  api.socket = wsRef.current;

  return api;
}

function sendReconnect(ws: WebSocket): void {
  const s = useGameStore.getState();
  if (s.roomCode && s.myPlayerId && s.identity) {
    ws.send(
      JSON.stringify({
        type: 'RECONNECT',
        roomCode: s.roomCode,
        playerId: s.myPlayerId,
        identity: s.identity,
      }),
    );
  }
}

function handleServerMessage(msg: ServerMessage): void {
  const s = useGameStore.getState();
  switch (msg.type) {
    case 'ERROR':
      s.setError(msg.message);
      window.setTimeout(() => s.setError(null), 4000);
      if (STALE_SEAT_CODES.has(msg.code)) {
        const urlCode = roomCodeFromUrl();
        if (urlCode) s.clearIdentityForRoom(urlCode);
        s.reset();
        if (urlCode) s.setPendingJoinCode(urlCode);
      }
      break;
    case 'ROOM_CREATED':
    case 'ROOM_JOINED':
      s.setConnectionInfo({ roomCode: msg.roomCode, playerId: msg.playerId, identity: msg.identity });
      navigateToRoom(msg.roomCode);
      break;
    case 'RECONNECTED':
      s.setError(null);
      if (s.roomCode) navigateToRoom(s.roomCode);
      break;
    case 'ROOM_SNAPSHOT':
      s.setSnapshot(msg.snapshot);
      break;
    case 'PRIVATE_STATE':
      s.setPrivateState(msg.state);
      break;
    case 'GAME_STARTED':
    case 'GAME_ENDED':
      s.setSnapshot(msg.snapshot);
      if ('state' in msg && msg.state) s.setPrivateState(msg.state);
      break;
    case 'CHAT':
      s.pushChat(msg.entry);
      break;
    case 'PLAYER_JOINED':
    case 'PLAYER_LEFT':
      break;
  }
}
